import { Injectable, inject } from '@angular/core'
import { HttpClient, HttpParams } from '@angular/common/http'
import { Observable, map } from 'rxjs'
import { FuseqHeaders } from '../../FuseQ/fuseq.headers'
import { environment } from '../../../environments/environment'
import type { WorkflowDefinition, WorkflowRun, WorkflowRunRequest, WorkflowRunResult } from '../../views/perimetroAWP_mediation/workflow/workflow.model'
import type { WorkflowActionDef } from '../../views/perimetroAWP_mediation/workflow/workflow-catalog'

export interface WorkflowValidationResult {
  valid: boolean
  errors: string[]
}

export interface WorkflowRunCounts {
  all: number
  active: number
  scheduled: number
  completed: number
  failed: number
  waiting: number
  cancelled: number
}

export interface WorkflowRunPage {
  items: WorkflowRun[]
  total: number
  limit: number
  offset: number
  counts: WorkflowRunCounts
}

export interface WorkflowRunQuery {
  limit?: number
  offset?: number
  fraz?: string
  status?: string
  q?: string
}

export type MediationWorkflowAction = WorkflowActionDef & { hasExecutor?: boolean }

@Injectable({
  providedIn: 'root'
})
export class AwpMediationWorkflowApiService {
  private readonly http = inject(HttpClient)
  private readonly fuseqHeaders = inject(FuseqHeaders)

  private base (): string {
    return `${environment.API_URL}/mediationWorkflow`
  }

  getTemplates (): Observable<WorkflowDefinition[]> {
    return this.http.get<WorkflowDefinition[]>(`${this.base()}/templates`, this.fuseqHeaders.setTokenHeaders('GET'))
  }

  getActions (): Observable<MediationWorkflowAction[]> {
    return this.http.get<MediationWorkflowAction[]>(`${this.base()}/actions`, this.fuseqHeaders.setTokenHeaders('GET'))
  }

  getTemplate (id: string): Observable<WorkflowDefinition> {
    return this.http.get<WorkflowDefinition>(`${this.base()}/templates/${id}`, this.fuseqHeaders.setTokenHeaders('GET'))
  }

  createTemplate (payload: WorkflowDefinition): Observable<WorkflowDefinition> {
    return this.http.post<WorkflowDefinition>(`${this.base()}/templates`, payload, this.fuseqHeaders.setTokenHeaders('POST'))
  }

  updateTemplate (id: string, payload: WorkflowDefinition): Observable<WorkflowDefinition> {
    return this.http.put<WorkflowDefinition>(`${this.base()}/templates/${id}`, payload, this.fuseqHeaders.setTokenHeaders('PUT'))
  }

  deleteTemplate (id: string): Observable<any> {
    return this.http.delete(`${this.base()}/templates/${id}`, this.fuseqHeaders.setTokenHeaders('DELETE'))
  }

  validate (payload: any): Observable<WorkflowValidationResult> {
    return this.http.post<WorkflowValidationResult>(`${this.base()}/validate`, payload, this.fuseqHeaders.setTokenHeaders('POST'))
  }

  getRuns (limit = 100, offset = 0): Observable<WorkflowRun[]> {
    return this.getRunsPage({ limit, offset }).pipe(map(page => page.items))
  }

  getRunsPage (query: WorkflowRunQuery = {}): Observable<WorkflowRunPage> {
    let params = new HttpParams()
      .set('limit', String(query.limit ?? 50))
      .set('offset', String(query.offset ?? 0))
    if (query.fraz) params = params.set('fraz', query.fraz)
    if (query.status) params = params.set('status', query.status)
    if (query.q) params = params.set('q', query.q)
    return this.http.get<WorkflowRunPage>(`${this.base()}/runs`, {
      ...this.fuseqHeaders.setTokenHeaders('GET'),
      params
    })
  }

  getRun (id: string): Observable<WorkflowRun> {
    return this.http.get<WorkflowRun>(`${this.base()}/runs/${id}`, this.fuseqHeaders.setTokenHeaders('GET'))
  }

  /**
   * SSE via fetch: EventSource non permette l'header AuthToken usato dalla
   * sessione AWP. Emette gli eventi `update` del backend.
   */
  streamRun (id: string): Observable<any> {
    return new Observable(subscriber => {
      const controller = new AbortController()
      const headers = this.fuseqHeaders.setTokenHeaders('GET').headers
      const requestHeaders: Record<string, string> = {}
      const allowedHeaders = ['authorization', 'authtoken', 'content-type']
      headers.keys()
        .filter(key => allowedHeaders.includes(key.toLowerCase()))
        .forEach(key => {
          const value = headers.get(key)
          if (value !== null) requestHeaders[key] = value
        })

      const read = async (): Promise<void> => {
        try {
          const response = await fetch(`${this.base()}/runs/${encodeURIComponent(id)}/stream`, {
            headers: requestHeaders,
            signal: controller.signal
          })
          if (!response.ok) throw new Error(`SSE HTTP ${response.status}`)
          if (!response.body) throw new Error('SSE body non disponibile')

          const reader = response.body.getReader()
          const decoder = new TextDecoder()
          let buffer = ''

          while (true) {
            const { value, done } = await reader.read()
            if (done) break
            buffer += decoder.decode(value, { stream: true })

            let boundary = buffer.indexOf('\n\n')
            while (boundary >= 0) {
              const chunk = buffer.slice(0, boundary)
              buffer = buffer.slice(boundary + 2)

              let event = 'message'
              const data: string[] = []
              chunk.split(/\r?\n/).forEach(line => {
                if (line.startsWith('event:')) event = line.slice(6).trim()
                else if (line.startsWith('data:')) data.push(line.slice(5).trim())
              })

              if (event === 'update' && data.length) {
                try {
                  subscriber.next(JSON.parse(data.join('\n')))
                } catch {
                  // ignora payload malformati: il polling di fallback resta disponibile
                }
              }
              if (event === 'error') {
                subscriber.error(new Error(data.join('\n') || 'SSE error'))
                controller.abort()
                return
              }
              if (event === 'end') {
                subscriber.complete()
                controller.abort()
                return
              }

              boundary = buffer.indexOf('\n\n')
            }
          }

          subscriber.complete()
        } catch (error) {
          if (!controller.signal.aborted) subscriber.error(error)
        }
      }

      read()
      return () => controller.abort()
    })
  }

  getRunResults (id: string): Observable<WorkflowRunResult[]> {
    return this.http.get<WorkflowRunResult[]>(`${this.base()}/runs/${id}/results`, this.fuseqHeaders.setTokenHeaders('GET'))
  }

  getRunStatus (id: string): Observable<any> {
    return this.http.get<any>(`${this.base()}/runs/${id}/status`, this.fuseqHeaders.setTokenHeaders('GET'))
  }

  createRun (payload: WorkflowRunRequest): Observable<WorkflowRun> {
    return this.http.post<WorkflowRun>(`${this.base()}/runs`, payload, this.fuseqHeaders.setTokenHeaders('POST'))
  }

  cancelRun (id: string): Observable<WorkflowRun> {
    return this.http.post<WorkflowRun>(`${this.base()}/runs/${id}/cancel`, {}, this.fuseqHeaders.setTokenHeaders('POST'))
  }

  retryRun (id: string): Observable<WorkflowRun> {
    return this.http.post<WorkflowRun>(`${this.base()}/runs/${id}/retry`, {}, this.fuseqHeaders.setTokenHeaders('POST'))
  }

  deleteRun (id: string): Observable<any> {
    return this.http.delete(`${this.base()}/runs/${id}`, this.fuseqHeaders.setTokenHeaders('DELETE'))
  }
}
